import { ProductResult } from "../types/ProductResult";

/**
 * Извлекает ключ группировки "Основное наименование" из raw данных товара
 *
 * @param product - Нормализованный продукт
 * @returns Ключ группировки или null, если ключ невалиден
 */
function extractGroupingKey(product: ProductResult): string | null {
  return extractMainName(product);
}

/**
 * Извлекает значение поля "Основное наименование" из raw.feed товара
 *
 * @param product - Нормализованный продукт
 * @returns "Основное наименование" или null, если поле невалидно
 */
function extractMainName(product: ProductResult): string | null {
  // Проверяем, что raw существует и является объектом
  if (!product.raw || typeof product.raw !== "object") {
    return null;
  }

  const rawObj = product.raw as Record<string, unknown>;

  // Проверяем наличие feed объекта
  if (!rawObj.feed || typeof rawObj.feed !== "object") {
    return null;
  }

  const feed = rawObj.feed as Record<string, unknown>;

  // Извлекаем значение поля "Основное наименование"
  const mainName = feed["Основное наименование"];

  // Значение валидно только если это непустая строка
  if (typeof mainName === "string" && mainName.trim().length > 0) {
    return mainName.trim();
  }

  return null;
}

/**
 * Группирует товары по полю "Основное наименование" из raw.feed
 *
 * Правила:
 * 1. Товары группируются только при наличии валидного ключа
 * 2. Если ключ отсутствует или невалиден — товар считается уникальным
 * 3. Для каждой группы выбирается первый товар (представитель)
 * 4. Представителю добавляется variantsCount = количество дополнительных вариантов
 * 5. Title представителя заменяется на "Основное наименование" (если валидно)
 * 6. Исходные объекты не модифицируются
 *
 * @param products - Массив нормализованных товаров
 * @returns Массив товаров с группировкой по вариантам
 */
export function productVariantsGrouper(products: ProductResult[]): ProductResult[] {
  // Если массив пуст или содержит один элемент — группировка не нужна
  if (products.length <= 1) {
    return products.map((product) => ({ ...product }));
  }

  // Map для группировки: ключ -> массив товаров
  const groups = new Map<string, ProductResult[]>();

  // Обрабатываем каждый товар
  for (const product of products) {
    const key = extractGroupingKey(product);

    if (key !== null) {
      // Валидный ключ — добавляем в соответствующую группу
      if (!groups.has(key)) {
        groups.set(key, []);
      }
      groups.get(key)!.push(product);
    } else {
      // Невалидный ключ — создаем уникальную группу с уникальным ключом
      const uniqueKey = `unique_${product.id}_${Date.now()}_${Math.random()}`;
      groups.set(uniqueKey, [product]);
    }
  }

  // Создаем результат: по одному представителю от каждой группы
  const result: ProductResult[] = [];

  for (const [_key, groupProducts] of groups) {
    // Берем первый товар из группы как представитель
    const representative = groupProducts[0];

    // Вычисляем количество дополнительных вариантов
    const variantsCount = groupProducts.length - 1;

    // Извлекаем "Основное наименование" для использования как title
    const mainName = extractMainName(representative);
    const title = mainName !== null ? mainName : representative.title;

    if (variantsCount > 0) {
      // Если какой-то из вариантов в наличии, вся карточка считается в наличии
      const isAnyAvailable = groupProducts.some((p) => p.available === true);
      const allExplicitlyUnavailable = groupProducts.every((p) => p.available === false);
      const available = isAnyAvailable ? true : allExplicitlyUnavailable ? false : representative.available;

      // Находим наименьшую цену среди всех вариантов
      const validPrices = groupProducts
        .map((p) => {
          if (typeof p.numericPrice === "number" && !isNaN(p.numericPrice) && p.numericPrice > 0) {
            return p.numericPrice;
          }
          if (typeof p.price === "string") {
            const num = Number(p.price.replace(/[^\d]/g, ""));
            if (!isNaN(num) && num > 0) return num;
          }
          return null;
        })
        .filter((price): price is number => price !== null);

      const minNumericPrice = validPrices.length > 0 ? Math.min(...validPrices) : representative.numericPrice;
      const price =
        minNumericPrice !== undefined
          ? `от ${minNumericPrice.toLocaleString("ru-RU")} ₽`
          : representative.price;

      const groupedProduct: ProductResult = {
        ...representative,
        title,
        variantsCount,
        price,
        numericPrice: minNumericPrice,
        available,
      };

      result.push(groupedProduct);
    } else {
      // Для одиночного товара оставляем исходные параметры
      const groupedProduct: ProductResult = {
        ...representative,
        title,
        variantsCount: undefined,
      };

      result.push(groupedProduct);
    }
  }

  return result;
}
